import { ConfigurationPreviewCard } from '@framework/ui/components/configuration-card/ConfigurationPreviewCard.tsx';
import { Feature, Map, View } from 'ol';
import { type Geometry, Point, Polygon } from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import { OSM } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import { useCallback, useEffect, useRef } from 'react';
import { OpenLayersMap } from '../../../../../maps/OpenLayerMap.tsx';
import { type IMapLayerStyle } from '../../layers/MapLayer.ts';
import { createVectorStyle } from '../../layers/rendering/createMapLayerFromConfig.ts';
import {
  DEFAULT_FILL,
  DEFAULT_STROKE,
  PreviewContainer,
} from './mapStylePreviewShared.ts';

export interface IMapLayerStylePreviewProps {
  /** Stile del layer da visualizzare in anteprima. */
  style?: IMapLayerStyle;
  /** Se true mostra anche una feature puntiforme con il simbolo. */
  showSymbol?: boolean;
}

// Centro dell'anteprima e disposizione delle geometrie di esempio
// (offset in metri EPSG:3857 rispetto al centro).
const PREVIEW_CENTER = fromLonLat([11.3426, 44.4949]);
const [CENTER_X, CENTER_Y] = PREVIEW_CENTER;
const PREVIEW_ZOOM = 15;

const POLYGON_COORDS = [
  [
    [CENTER_X - 450, CENTER_Y - 150],
    [CENTER_X - 100, CENTER_Y - 200],
    [CENTER_X - 150, CENTER_Y + 180],
    [CENTER_X - 400, CENTER_Y + 120],
    [CENTER_X - 450, CENTER_Y - 150],
  ],
];
const POINT_COORD = [CENTER_X + 300, CENTER_Y];

/**
 * Costruisce lo stile OL dell'anteprima riutilizzando la stessa funzione
 * usata dal rendering runtime dei layer vettoriali: quello che si vede in
 * anteprima è quello che si vedrà in mappa.
 */
function createPreviewStyle(style: IMapLayerStyle | undefined) {
  const previewStyle: IMapLayerStyle = {
    ...style,
    fillColor: style?.fillColor ?? DEFAULT_FILL,
    strokeColor: style?.strokeColor ?? DEFAULT_STROKE,
    strokeWidth: style?.strokeWidth ?? 2,
  };
  return createVectorStyle(previewStyle);
}

/**
 * Anteprima dello stile di un layer vettoriale su una mappa OSM reale:
 * un poligono per riempimento/bordo e, se richiesto, una feature
 * puntiforme con il simbolo configurato.
 */
export function MapLayerStylePreview(props: IMapLayerStylePreviewProps) {
  const { style, showSymbol = false } = props;

  const vectorLayerRef = useRef<VectorLayer<Feature<Geometry>> | null>(null);
  const pointFeatureRef = useRef<Feature<Geometry> | null>(null);
  const sourceRef = useRef<VectorSource<Feature<Geometry>> | null>(null);

  // Stato iniziale per initMap, che viene eseguito una sola volta.
  const initialProps = useRef(props);
  initialProps.current = props;

  const initMap = useCallback((element: HTMLDivElement) => {
    const { style: initialStyle, showSymbol: initialShowSymbol } =
      initialProps.current;

    const polygonFeature = new Feature<Geometry>(new Polygon(POLYGON_COORDS));
    const pointFeature = new Feature<Geometry>(new Point(POINT_COORD));
    pointFeatureRef.current = pointFeature;

    const source = new VectorSource<Feature<Geometry>>({
      features: [polygonFeature],
    });
    if (initialShowSymbol) source.addFeature(pointFeature);
    sourceRef.current = source;

    const vectorLayer = new VectorLayer({
      source,
      style: createPreviewStyle(initialStyle),
      opacity: initialStyle?.opacity ?? 1,
    });
    vectorLayerRef.current = vectorLayer;

    return new Map({
      target: element,
      // Anteprima statica: nessuna interazione di pan/zoom
      interactions: [],
      layers: [new TileLayer({ source: new OSM() }), vectorLayer],
      view: new View({ center: PREVIEW_CENTER, zoom: PREVIEW_ZOOM }),
    });
  }, []);

  // Aggiorna stile, opacità e visibilità del punto al variare della config.
  useEffect(() => {
    const vectorLayer = vectorLayerRef.current;
    const source = sourceRef.current;
    const pointFeature = pointFeatureRef.current;
    if (!vectorLayer || !source || !pointFeature) return;

    vectorLayer.setStyle(createPreviewStyle(style));
    vectorLayer.setOpacity(style?.opacity ?? 1);

    const hasPoint = source.hasFeature(pointFeature);
    if (showSymbol && !hasPoint) source.addFeature(pointFeature);
    if (!showSymbol && hasPoint) source.removeFeature(pointFeature);
  }, [style, showSymbol]);

  return (
    <PreviewContainer>
      <ConfigurationPreviewCard>
        <OpenLayersMap init={initMap} style={{ height: '200px' }} />
      </ConfigurationPreviewCard>
    </PreviewContainer>
  );
}
